// Валидация и (де)сериализация AppState. Не импортирует src/core.
import type { AppState, CurrencyDef } from '../types';
import {
  BASE_CURRENCY,
  CURRENCY_CODE_RE,
  DEFAULT_CURRENCIES,
  DEFAULT_EUR_USD,
  MAX_VARIANTS,
} from '../types';

export const STORAGE_KEY = 'plan-flights.state.v1';

export type ValidationResult =
  | { ok: true; state: AppState }
  | { ok: false; errors: string[] };

const LOCAL_DATETIME_RE =
  /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d$/;
const IATA_RE = /^[A-Z]{3}$/;
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const PRICE_SCOPES = new Set(['one_way', 'round_trip_half', 'round_trip_full']);
const SEGMENT_ORIGINS = new Set(['manual', 'text', 'url', 'screenshot']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isInteger(v: unknown): v is number {
  return isFiniteNumber(v) && Number.isInteger(v);
}

function validateMoney(path: string, raw: unknown, errors: string[]): void {
  if (!isRecord(raw)) {
    errors.push(`${path}: ожидался объект Money {amount, currency}`);
    return;
  }
  const amount = raw['amount'];
  if (!isFiniteNumber(amount)) {
    errors.push(`${path}.amount: ожидалось число`);
  } else if (!Number.isInteger(amount)) {
    errors.push(`${path}.amount: ожидалось целое число (минорные единицы), получено дробное значение ${amount}`);
  }
  // Здесь проверяется только форма кода: то, что валюта есть в справочнике,
  // проверяется отдельно — справочник живёт на верхнем уровне состояния.
  const currency = raw['currency'];
  if (!isString(currency) || !CURRENCY_CODE_RE.test(currency)) {
    errors.push(
      `${path}.currency: ожидался код валюты из 3 заглавных букв, получено ${JSON.stringify(currency)}`,
    );
  }
}

function validateOptionalString(path: string, raw: unknown, errors: string[]): void {
  if (raw !== undefined && !isString(raw)) {
    errors.push(`${path}: ожидалась строка`);
  }
}

function validateOptionalBoolean(path: string, raw: unknown, errors: string[]): void {
  if (raw !== undefined && typeof raw !== 'boolean') {
    errors.push(`${path}: ожидалось логическое значение`);
  }
}

/**
 * Общие для всех типов сегментов поля SegmentMeta (addedAt/origin, см.
 * types.ts) — оба опциональны: старые сохранения, заведённые до появления
 * этой фичи, читаются как есть, блок без отметки просто считается старым.
 */
function validateSegmentMeta(path: string, raw: Record<string, unknown>, errors: string[]): void {
  const addedAt = raw['addedAt'];
  if (addedAt !== undefined && (!isInteger(addedAt) || addedAt < 0)) {
    errors.push(`${path}.addedAt: ожидалось целое число >= 0 (миллисекунды эпохи), получено ${JSON.stringify(addedAt)}`);
  }
  const origin = raw['origin'];
  if (origin !== undefined && (!isString(origin) || !SEGMENT_ORIGINS.has(origin))) {
    errors.push(
      `${path}.origin: ожидалось одно из 'manual' | 'text' | 'url' | 'screenshot', получено ${JSON.stringify(origin)}`,
    );
  }
}

function validateFlight(
  path: string,
  raw: Record<string, unknown>,
  errors: string[],
  flightIds: Set<string>,
): void {
  const from = raw['from'];
  if (!isString(from) || !IATA_RE.test(from)) {
    errors.push(`${path}.from: ожидался код IATA из 3 латинских букв в верхнем регистре, получено ${JSON.stringify(from)}`);
  }
  const to = raw['to'];
  if (!isString(to) || !IATA_RE.test(to)) {
    errors.push(`${path}.to: ожидался код IATA из 3 латинских букв в верхнем регистре, получено ${JSON.stringify(to)}`);
  }
  if (!isString(raw['fromTz'])) {
    errors.push(`${path}.fromTz: ожидалась строка (таймзона IANA либо '')`);
  }
  if (!isString(raw['toTz'])) {
    errors.push(`${path}.toTz: ожидалась строка (таймзона IANA либо '')`);
  }
  const departLocal = raw['departLocal'];
  if (!isString(departLocal) || !LOCAL_DATETIME_RE.test(departLocal)) {
    errors.push(`${path}.departLocal: ожидался формат 'YYYY-MM-DDTHH:mm', получено ${JSON.stringify(departLocal)}`);
  }
  const arriveLocal = raw['arriveLocal'];
  if (!isString(arriveLocal) || !LOCAL_DATETIME_RE.test(arriveLocal)) {
    errors.push(`${path}.arriveLocal: ожидался формат 'YYYY-MM-DDTHH:mm', получено ${JSON.stringify(arriveLocal)}`);
  }
  if (!isString(raw['carrier'])) {
    errors.push(`${path}.carrier: ожидалась строка`);
  }
  validateOptionalString(`${path}.flightNumber`, raw['flightNumber'], errors);
  validateOptionalString(`${path}.url`, raw['url'], errors);
  validateMoney(`${path}.price`, raw['price'], errors);
  const priceScope = raw['priceScope'];
  if (!isString(priceScope) || !PRICE_SCOPES.has(priceScope)) {
    errors.push(`${path}.priceScope: ожидалось одно из 'one_way' | 'round_trip_half' | 'round_trip_full', получено ${JSON.stringify(priceScope)}`);
  }
  const linkedTo = raw['linkedTo'];
  if (linkedTo !== undefined) {
    if (!isString(linkedTo)) {
      errors.push(`${path}.linkedTo: ожидалась строка`);
    } else if (!flightIds.has(linkedTo)) {
      errors.push(`${path}.linkedTo: ссылается на несуществующий рейс "${linkedTo}"`);
    }
  }
  validateOptionalString(`${path}.note`, raw['note'], errors);
  validateSegmentMeta(path, raw, errors);
}

function validateTransfer(path: string, raw: Record<string, unknown>, errors: string[]): void {
  if (!isString(raw['from'])) errors.push(`${path}.from: ожидалась строка`);
  if (!isString(raw['to'])) errors.push(`${path}.to: ожидалась строка`);
  const durationMin = raw['durationMin'];
  if (!isInteger(durationMin) || durationMin < 0) {
    errors.push(`${path}.durationMin: ожидалось целое число >= 0, получено ${JSON.stringify(durationMin)}`);
  }
  validateMoney(`${path}.price`, raw['price'], errors);
  validateOptionalBoolean(`${path}.perPerson`, raw['perPerson'], errors);
  validateOptionalString(`${path}.note`, raw['note'], errors);
  validateSegmentMeta(path, raw, errors);
}

function validateLodging(path: string, raw: Record<string, unknown>, errors: string[]): void {
  if (!isString(raw['city'])) errors.push(`${path}.city: ожидалась строка`);
  const nights = raw['nights'];
  if (!isInteger(nights) || nights < 1) {
    errors.push(`${path}.nights: ожидалось целое число >= 1, получено ${JSON.stringify(nights)}`);
  }
  validateMoney(`${path}.pricePerNight`, raw['pricePerNight'], errors);
  validateOptionalBoolean(`${path}.perPerson`, raw['perPerson'], errors);
  validateOptionalString(`${path}.note`, raw['note'], errors);
  validateSegmentMeta(path, raw, errors);
}

function validateSimpleCost(path: string, raw: Record<string, unknown>, errors: string[]): void {
  if (!isString(raw['label'])) errors.push(`${path}.label: ожидалась строка`);
  validateOptionalString(`${path}.city`, raw['city'], errors);
  validateMoney(`${path}.price`, raw['price'], errors);
  const quantity = raw['quantity'];
  if (!isInteger(quantity) || quantity < 1) {
    errors.push(`${path}.quantity: ожидалось целое число >= 1, получено ${JSON.stringify(quantity)}`);
  }
  validateOptionalString(`${path}.note`, raw['note'], errors);
  validateSegmentMeta(path, raw, errors);
}

function validateBaggage(
  path: string,
  raw: Record<string, unknown>,
  errors: string[],
  flightIds: Set<string>,
): void {
  const flightId = raw['flightId'];
  if (!isString(flightId)) {
    errors.push(`${path}.flightId: ожидалась строка`);
  } else if (!flightIds.has(flightId)) {
    errors.push(`${path}.flightId: ссылается на несуществующий рейс "${flightId}"`);
  }
  if (!isString(raw['label'])) errors.push(`${path}.label: ожидалась строка`);
  validateMoney(`${path}.price`, raw['price'], errors);
  validateSegmentMeta(path, raw, errors);
}

function validateSegment(
  id: string,
  raw: unknown,
  errors: string[],
  flightIds: Set<string>,
): void {
  const path = `library.${id}`;
  if (!isRecord(raw)) {
    errors.push(`${path}: ожидался объект`);
    return;
  }
  if (!isString(raw['id']) || raw['id'].length === 0) {
    errors.push(`${path}.id: ожидалась непустая строка`);
  }
  const kind = raw['kind'];
  switch (kind) {
    case 'flight':
      validateFlight(path, raw, errors, flightIds);
      break;
    case 'transfer':
      validateTransfer(path, raw, errors);
      break;
    case 'lodging':
      validateLodging(path, raw, errors);
      break;
    case 'taxi':
    case 'food':
    case 'misc':
      validateSimpleCost(path, raw, errors);
      break;
    case 'baggage':
      validateBaggage(path, raw, errors, flightIds);
      break;
    default:
      errors.push(`${path}.kind: недопустимое значение ${JSON.stringify(kind)}`);
  }
}

function validateVariant(
  idx: number,
  raw: unknown,
  errors: string[],
  libraryIds: Set<string>,
): void {
  const path = `variants.${idx}`;
  if (!isRecord(raw)) {
    errors.push(`${path}: ожидался объект`);
    return;
  }
  if (!isString(raw['id']) || raw['id'].length === 0) {
    errors.push(`${path}.id: ожидалась непустая строка`);
  }
  if (!isString(raw['name'])) {
    errors.push(`${path}.name: ожидалась строка`);
  }
  const color = raw['color'];
  if (!isString(color) || !HEX_COLOR_RE.test(color)) {
    errors.push(`${path}.color: ожидался hex-цвет вида '#rrggbb', получено ${JSON.stringify(color)}`);
  }
  validateOptionalBoolean(`${path}.locked`, raw['locked'], errors);
  validateOptionalBoolean(`${path}.collapsed`, raw['collapsed'], errors);
  const segmentIds = raw['segmentIds'];
  if (!Array.isArray(segmentIds)) {
    errors.push(`${path}.segmentIds: ожидался массив`);
  } else {
    segmentIds.forEach((sid, i) => {
      if (!isString(sid)) {
        errors.push(`${path}.segmentIds.${i}: ожидалась строка`);
      } else if (!libraryIds.has(sid)) {
        errors.push(`${path}.segmentIds.${i}: ссылается на несуществующий сегмент "${sid}"`);
      }
    });
  }
}

/**
 * Справочник валют. Состояния, сохранённые до появления пользовательских валют,
 * несут вместо него скаляр `eurUsdRate` — из него собирается USD + EUR, чтобы
 * старый экспорт и старая запись в localStorage продолжали открываться.
 */
function validateCurrencies(raw: Record<string, unknown>, errors: string[]): CurrencyDef[] {
  const currenciesRaw = raw['currencies'];

  if (currenciesRaw === undefined) {
    const legacyRate = raw['eurUsdRate'];
    const rate = isFiniteNumber(legacyRate) && legacyRate > 0 ? legacyRate : DEFAULT_EUR_USD;
    return DEFAULT_CURRENCIES.map((c) =>
      c.code === 'EUR' ? { ...c, rateToBase: rate } : { ...c },
    );
  }

  if (!Array.isArray(currenciesRaw)) {
    errors.push('currencies: ожидался массив');
    return [];
  }

  const result: CurrencyDef[] = [];
  const seen = new Set<string>();
  currenciesRaw.forEach((entry, idx) => {
    const path = `currencies.${idx}`;
    if (!isRecord(entry)) {
      errors.push(`${path}: ожидался объект`);
      return;
    }
    const code = entry['code'];
    if (!isString(code) || !CURRENCY_CODE_RE.test(code)) {
      errors.push(`${path}.code: ожидался код из 3 заглавных букв, получено ${JSON.stringify(code)}`);
      return;
    }
    if (seen.has(code)) {
      errors.push(`${path}.code: валюта "${code}" встречается дважды`);
      return;
    }
    seen.add(code);

    const symbol = entry['symbol'];
    if (!isString(symbol)) {
      errors.push(`${path}.symbol: ожидалась строка`);
    }
    const rate = entry['rateToBase'];
    if (!isFiniteNumber(rate) || !(rate > 0)) {
      errors.push(`${path}.rateToBase: ожидалось число > 0, получено ${JSON.stringify(rate)}`);
    }
    if (code === BASE_CURRENCY && isFiniteNumber(rate) && rate !== 1) {
      errors.push(`${path}.rateToBase: у базовой валюты ${BASE_CURRENCY} курс должен быть 1`);
    }

    if (isString(symbol) && isFiniteNumber(rate) && rate > 0) {
      result.push({ code, symbol, rateToBase: rate });
    }
  });

  if (!seen.has(BASE_CURRENCY)) {
    errors.push(`currencies: справочник обязан содержать базовую валюту ${BASE_CURRENCY}`);
  }

  return result;
}

/** Полная валидация схемы AppState. Собирает ВСЕ ошибки, не бросает исключений. */
export function validateAppState(raw: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isRecord(raw)) {
    return { ok: false, errors: ['state: ожидался объект'] };
  }

  const libraryRaw = raw['library'];
  const libraryIds = new Set<string>();
  const flightIds = new Set<string>();
  if (!isRecord(libraryRaw)) {
    errors.push('library: ожидался объект');
  } else {
    for (const key of Object.keys(libraryRaw)) {
      libraryIds.add(key);
      const entry = libraryRaw[key];
      if (isRecord(entry) && entry['kind'] === 'flight') {
        flightIds.add(key);
      }
    }
    for (const [key, segRaw] of Object.entries(libraryRaw)) {
      validateSegment(key, segRaw, errors, flightIds);
    }
  }

  const variantsRaw = raw['variants'];
  if (!Array.isArray(variantsRaw)) {
    errors.push('variants: ожидался массив');
  } else {
    if (variantsRaw.length > MAX_VARIANTS) {
      errors.push(`variants: превышено допустимое количество вариантов (макс. ${MAX_VARIANTS}, получено ${variantsRaw.length})`);
    }
    variantsRaw.forEach((v, idx) => validateVariant(idx, v, errors, libraryIds));
  }

  const travelers = raw['travelers'];
  if (!isInteger(travelers) || travelers < 1) {
    errors.push(`travelers: ожидалось целое число >= 1, получено ${JSON.stringify(travelers)}`);
  }

  const currencies = validateCurrencies(raw, errors);
  const codes = new Set(currencies.map((c) => c.code));

  const displayCurrency = raw['displayCurrency'];
  if (!isString(displayCurrency) || !CURRENCY_CODE_RE.test(displayCurrency)) {
    errors.push(
      `displayCurrency: ожидался код валюты из 3 заглавных букв, получено ${JSON.stringify(displayCurrency)}`,
    );
  } else if (codes.size > 0 && !codes.has(displayCurrency)) {
    errors.push(`displayCurrency: валюты "${displayCurrency}" нет в справочнике currencies`);
  }

  // Цена в валюте, которой нет в справочнике, посчиталась бы по курсу 1 —
  // это молчаливо неверные итоги, поэтому такой импорт отклоняем.
  if (codes.size > 0 && isRecord(libraryRaw)) {
    const unknown = new Set<string>();
    for (const key of Object.keys(libraryRaw)) {
      const entry = libraryRaw[key];
      if (!isRecord(entry)) continue;
      const moneyRaw = entry['kind'] === 'lodging' ? entry['pricePerNight'] : entry['price'];
      if (!isRecord(moneyRaw)) continue;
      const code = moneyRaw['currency'];
      if (isString(code) && CURRENCY_CODE_RE.test(code) && !codes.has(code)) unknown.add(code);
    }
    for (const code of unknown) {
      errors.push(`library: цены в валюте "${code}", которой нет в справочнике currencies`);
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }
  // eurUsdRate из старых сохранений отбрасываем: справочник currencies его заменил.
  const { eurUsdRate: _legacyRate, ...rest } = raw;
  return { ok: true, state: { ...rest, currencies } as unknown as AppState };
}

/** Рекурсивно сортирует ключи объектов (массивы оставляет в исходном порядке). */
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (isRecord(value)) {
    const sortedKeys = Object.keys(value).sort();
    const result: Record<string, unknown> = {};
    for (const key of sortedKeys) {
      result[key] = sortKeysDeep(value[key]);
    }
    return result;
  }
  return value;
}

/** Детерминированный JSON: ключи объектов сортируются рекурсивно, отступ 2, завершающий '\n'. */
export function serializeState(s: AppState): string {
  const sorted = sortKeysDeep(s as unknown);
  return JSON.stringify(sorted, null, 2) + '\n';
}

export function parseStateJson(text: string): ValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      errors: [`JSON: не удалось разобрать текст (${e instanceof Error ? e.message : String(e)})`],
    };
  }
  return validateAppState(parsed);
}
